import type { Agreement } from './lib/agreement'

/** Longer text blocks and tool results start collapsed to a preview. */
export const LONG_TEXT_CHARS = 1200
export const LONG_TEXT_LINES = 20

/** Tool inputs up to this many lines of pretty JSON start expanded. */
export const SHORT_JSON_LINES = 12

/** The look and words of each agreement, used everywhere a value is compared to the final one. */
export const AGREEMENT_STYLES: Record<
  Agreement,
  { label: string; icon: string; text: string; fill: string; dot: string; ring: string }
> = {
  agree: {
    label: 'Agrees',
    icon: 'check_circle',
    text: 'text-success-content',
    fill: 'bg-alert-success-fill',
    dot: 'bg-alert-success-message',
    ring: 'ring-alert-success-message/40',
  },
  partial: {
    label: 'Partly agrees',
    icon: 'contrast',
    text: 'text-swatch-yellow-900',
    fill: 'bg-swatch-yellow-100',
    dot: 'bg-swatch-yellow-60',
    ring: 'ring-swatch-yellow-60/50',
  },
  differ: {
    label: 'Differs',
    icon: 'cancel',
    text: 'text-alert-error-content',
    fill: 'bg-alert-error-fill',
    dot: 'bg-alert-error-message',
    ring: 'ring-alert-error-message/40',
  },
  pending: {
    label: 'Awaiting review',
    icon: 'schedule',
    text: 'text-text-secondary',
    fill: 'bg-surface-low',
    dot: 'bg-content-low-emphasis',
    ring: 'ring-divider',
  },
  none: {
    label: 'Nothing to compare',
    icon: 'remove',
    text: 'text-text-tertiary',
    fill: 'bg-surface-low',
    dot: 'bg-content-lowest-emphasis',
    ring: 'ring-divider',
  },
}

export const AGREEMENT_LEGEND: Array<{ agreement: Agreement; label: string }> = [
  { agreement: 'agree', label: 'Same as final' },
  { agreement: 'partial', label: 'Partly the same' },
  { agreement: 'differ', label: 'Different from final' },
  { agreement: 'pending', label: 'Not reviewed yet' },
]

/** Who produced a value. The final state is the reference every other source is compared to. */
export const SOURCES = {
  final: {
    label: 'Final',
    caption: 'after review',
    help: 'What the document ended as once a reviewer (or an auto-review rule) settled it. This is the reference: the agent and production are compared to it.',
  },
  agent: {
    label: 'Agent',
    caption: 'this run',
    help: 'What the agent produced in this run. The agent runs in shadow mode: it never changes the document.',
  },
  today: {
    label: 'Production',
    caption: "today's pipeline",
    help: "What today's production pipeline produced when the document arrived, before anyone reviewed it.",
  },
} as const

export type SourceKey = keyof typeof SOURCES

export const FIELD_HELP = {
  category:
    'The document type, made of a phase (e.g. Claim appeal), an outcome (e.g. Denied upheld) and, for some outcomes, a reason. A category partly agrees when some of these three parts are the same.',
  codes:
    'CARC and RARC denial codes and Natera tags (such as appeal deadlines) found in the document. Compared code by code: a code the final state has and the agent lacks is missed; a code the agent has and the final state lacks is extra.',
  match:
    "The patient case the document belongs to. Scored only on the document's side (claim or prior authorization), against the case the reviewer settled on.",
} as const

/** Actors on the timeline. */
export const ACTORS = {
  production: { label: 'Production', icon: 'settings', dot: 'bg-slate-500' },
  agent: { label: 'Agent', icon: 'auto_awesome', dot: 'bg-primary-main' },
  reviewer: { label: 'Reviewer', icon: 'person', dot: 'bg-violet-600' },
} as const

export type Actor = keyof typeof ACTORS

/** Candidate row fields shown as columns, in order; every field is still in the row JSON below the table. */
export const MATCH_ROW_COLUMNS = {
  claim: [
    { key: 'lims_casefile_id', label: 'Casefile' },
    { key: 'name', label: 'Patient' },
    { key: 'date_of_birth', label: 'DOB' },
    { key: 'lims_date_of_service', label: 'DOS' },
    { key: 'test_name', label: 'Test' },
    { key: 'payorname', label: 'Payor' },
    { key: 'policy_number', label: 'Policy' },
    { key: 'claim_id', label: 'Claim ID' },
    { key: 'similarity', label: 'Similarity' },
    { key: 'matched_on', label: 'Matched on' },
    { key: 'contradicted_on', label: 'Contradicted on' },
  ],
  pa: [
    { key: 'case_id', label: 'Case' },
    { key: 'temporary_lims_casefile_id', label: 'Temp casefile' },
    { key: 'name', label: 'Patient' },
    { key: 'patient_dob', label: 'DOB' },
    { key: 'service_date', label: 'Service date' },
    { key: 'test_name', label: 'Test' },
    { key: 'payor_group', label: 'Payor' },
    { key: 'policy_number', label: 'Policy' },
    { key: 'similarity', label: 'Similarity' },
    { key: 'matched_on', label: 'Matched on' },
    { key: 'contradicted_on', label: 'Contradicted on' },
  ],
} as const

/** Marks on a candidate row, in the order they are shown. */
export const MATCH_ROW_MARKS = [
  {
    key: 'reviewer',
    label: 'Final',
    description: "The reviewer's final case",
    className: 'bg-alert-success-fill text-success-content',
  },
  {
    key: 'agent',
    label: 'Agent',
    description: "The agent's candidate",
    className: 'bg-primary-selected text-primary-dark',
  },
  {
    key: 'tied',
    label: 'Tied',
    description: 'One of the rows the agent could not choose between',
    className: 'bg-swatch-yellow-100 text-swatch-yellow-900',
  },
  {
    key: 'today',
    label: 'Production',
    description: "Production's rule pick when the document arrived",
    className: 'bg-surface-medium text-text-primary',
  },
  {
    key: 'rules',
    label: 'Rules',
    description: "Today's rules, re-run on the agent's rows",
    className: 'border border-divider text-text-secondary',
  },
] as const

/** Words for the matching tool's criteria. */
export const MATCH_CRITERIA_LABELS: Record<string, string> = {
  patient: 'patient',
  subscriber: 'subscriber',
  patient_dob: 'date of birth',
  member_id: 'member ID',
  service_date_from: 'service date',
  service_date_to: 'service end date',
  cpt_codes: 'CPT codes',
  test_names: 'test',
  claim_number: 'claim number',
  appeal_id: 'appeal ID',
  authorization_code: 'authorization code',
  case_number: 'case number',
  reference_number: 'reference number',
  service_request_number: 'service request number',
  patient_account_number: 'patient account number',
  document_number: 'document number',
  barcode: 'barcode',
  other_identifiers: 'other identifiers',
  total_amount: 'total amount',
}

export const MATCH_STATUS_NOTES: Record<string, string> = {
  deferred:
    'Waiting for a case-lookup ingestion window to end; the matching session has not run yet.',
  not_applicable:
    "The agent's category is not one that needs a patient case (or is an EOB), so no matching session ran.",
}

/** Audit events the timeline leaves out: shadow models and internal bookkeeping. */
export const HIDDEN_AUDIT_EVENTS = new Set(['mbclass', 'unknown', 'patient', 'pa-patient'])
